/**
 * src/features/auth/ui/GoogleSignInButton.tsx
 *
 * GoogleSignInButton — one-click Google OAuth entry point.
 *
 * AUTH-06: clicking calls authClient.signIn.social, which is a FULL-PAGE NAVIGATION
 * (redirectPlugin sets window.location.href to the Google authorize URL), NOT a fetch.
 * The browser physically leaves the page on success (42-RESEARCH.md Pattern 2), so no
 * post-call state update is meaningful on success — only the synchronous `disabled` on
 * click matters (it guards against a second navigation firing before the browser leaves).
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authClient } from '@/lib/auth-client';
import Button from '@/shared/ui/button';
import {
  captureGoogleRollbackCandidate,
  consumeGoogleRollbackCandidate,
  getLastLoginMethod,
  setLastLoginMethod,
} from '../lib/lastLoginMethod';

interface GoogleSignInButtonProps {
  /** Seeds the error banner from a Google OAuth callback failure LoginCard already detected
   * via its own `?error=` presence read (see LoginCard.tsx's `hasGoogleCallbackError`
   * comment). Passing it down here — instead of LoginCard owning a second, independent
   * `role="alert"` node — keeps exactly one Google error banner mounted at a time (WR-01:
   * two independently-owned alert nodes could previously coexist and double-announce to
   * screen readers when a callback error was present AND a retry click failed). */
  initialError?: string | null;
}

export const GoogleSignInButton: FC<GoogleSignInButtonProps> = ({ initialError = null }) => {
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  const handleClick = async () => {
    setIsDisabled(true);
    setError(null);

    // AUTH-09 / this plan's flagged-assumption resolution — Google has no client-observed
    // moment of confirmed success on this screen (see the file-header comment: the browser
    // physically navigates away). The write below is optimistic, with two rollback paths:
    //   1. There is no success moment to observe here at all — observing one would mean
    //      reaching past useAuth/RequireAuth, outside this phase's boundary.
    //   2. Immediate failure — the call returns { error } before navigation happens. Handled
    //      synchronously below.
    //   3. Late failure (WR-02) — the browser physically leaves this page, then returns via
    //      errorCallbackURL: '/' with a query-string error (user backs out on Google's own
    //      screen, the provider declines, the account-linking gate rejects). This code never
    //      re-runs for that case, so captureGoogleRollbackCandidate hands the method that was
    //      remembered before this click to LoginCard, which restores it on the return trip if
    //      (and only if) it reads that error param. This is NOT a no-op case: 'google' and
    //      "nothing remembered" render identically, but 'google' vs. a real, different prior
    //      preference (e.g. 'password') does not — silently discarding that preference on an
    //      attempt that may never complete is a real regression against AUTH-09, not merely a
    //      first-time-user edge case.
    const previousMethod = getLastLoginMethod();
    captureGoogleRollbackCandidate(previousMethod);
    setLastLoginMethod('google');

    // WR-01/Pattern 1: better-auth returns { data, error } and NEVER throws — branch on
    // result.error below, not try/catch.
    // errorCallbackURL: '/' is required alongside callbackURL: '/'. Without it, a failed
    // OAuth callback strands the user on better-auth's own unbranded HTML error page in
    // every environment: /api/auth/error only redirects to '/' when isProduction===true,
    // and isProduction reads process.env.NODE_ENV, which workerd never sets in this
    // deployment (42-RESEARCH.md Summary п.2 / Pitfall 4, same root cause documented for
    // rateLimit.enabled in Phase 39).
    const result = await authClient.signIn.social({
      provider: 'google',
      callbackURL: '/',
      errorCallbackURL: '/',
    });
    // Nothing below runs on success — the browser has already navigated away to Google.
    if (result.error) {
      // Immediate failure — navigation never happened. Roll back to whatever was
      // remembered before this click (point 2 above), and drop the rollback candidate:
      // this path already handled the restore synchronously, so LoginCard must not also
      // act on a stale candidate on some later, unrelated mount.
      setLastLoginMethod(previousMethod);
      consumeGoogleRollbackCandidate();
      setError(t('auth.google.error', 'Google sign-in failed. Please try again.'));
      setIsDisabled(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="outline"
        size="lg"
        className="w-full gap-2"
        disabled={isDisabled}
        onClick={() => void handleClick()}
      >
        {/* Google "G" glyph per Google Identity brand guidelines — lucide-react has no
            brand icons (42-UI-SPEC.md § Design System), so this is a static inline SVG.
            aria-hidden: the button's own text carries the accessible name. */}
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M19.6 10.23c0-.68-.06-1.33-.17-1.96H10v3.71h5.38a4.6 4.6 0 01-2 3.02v2.5h3.23c1.89-1.74 2.99-4.3 2.99-7.27z"
          />
          <path
            fill="#34A853"
            d="M10 20c2.7 0 4.96-.89 6.62-2.42l-3.23-2.5c-.9.6-2.04.96-3.39.96-2.6 0-4.81-1.76-5.6-4.12H1.06v2.59A10 10 0 0010 20z"
          />
          <path
            fill="#FBBC05"
            d="M4.4 11.92A5.99 5.99 0 014.09 10c0-.67.11-1.32.31-1.92V5.49H1.06A10 10 0 000 10c0 1.61.39 3.14 1.06 4.51l3.34-2.59z"
          />
          <path
            fill="#EA4335"
            d="M10 3.96c1.47 0 2.79.5 3.83 1.5l2.87-2.87C14.95.99 12.7 0 10 0 6.09 0 2.71 2.24 1.06 5.49l3.34 2.59C5.19 5.72 7.4 3.96 10 3.96z"
          />
        </svg>
        {t('auth.google.cta', 'Continue with Google')}
      </Button>
      {error && (
        <p className="text-body text-red-600 dark:text-red-400 text-center" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
